import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemText, ListItemIcon, IconButton, TextField, InputAdornment, Divider, Button, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CircleIcon from '@mui/icons-material/Circle';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api';

const CommunicationCenter = ({ channel, title }) => {
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); 
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [attachments, setAttachments] = useState([]);
  
  // THE FIX: Added a reference to the reading area so we can manipulate the images after they render
  const contentRef = useRef(null);
  
  const empId = localStorage.getItem('emp_id');

  useEffect(() => {
    fetchEmails();
    setSelectedEmail(null);
    setAttachments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    applyFilters(searchQuery, statusFilter, emails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, emails]);

  // THE FIX: Securely intercept and load inline images using the authenticated API
  useEffect(() => {
    if (selectedEmail && contentRef.current) {
      const loadInlineImages = async () => {
        const images = contentRef.current.querySelectorAll('img');
        
        images.forEach(async (img) => {
          const src = img.getAttribute('src');
          // If the image source points to our secure API inline route
          if (src && src.includes('/inline/')) {
            try {
              // Fetch the image securely as a blob using the Axios auth token
              const response = await api.get(src, { responseType: 'blob' });
              const objectUrl = URL.createObjectURL(response.data);
              img.src = objectUrl; // Replace the broken link with the downloaded image data
            } catch (err) {
              console.error("Failed to load inline image", err);
            }
          }
        });
      };
      
      loadInlineImages();
    }
  }, [selectedEmail]);

  const fetchEmails = async () => {
    try {
      const res = await api.get(`/communications/${channel}?emp_id=${empId}`);
      setEmails(res.data);
    } catch (err) {
      console.error("Failed to fetch communications", err);
    }
  };

  const applyFilters = (search, status, sourceEmails) => {
    const query = search.toLowerCase();
    const filtered = sourceEmails.filter(email => {
      const matchesSearch = email.subject.toLowerCase().includes(query) || 
                            email.sender_name.toLowerCase().includes(query) ||
                            email.body_html.toLowerCase().includes(query);
      
      let matchesStatus = true;
      if (status === 'UNREAD') matchesStatus = !email.is_read;
      if (status === 'READ') matchesStatus = email.is_read;
      if (status === 'FLAGGED') matchesStatus = email.is_flagged;

      return matchesSearch && matchesStatus;
    });
    setFilteredEmails(filtered);
  };

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    
    if (!email.is_read) {
      const updatedList = emails.map(e => e.id === email.id ? { ...e, is_read: true } : e);
      setEmails(updatedList);
    }

    try {
      const res = await api.get(`/communications/${email.id}/details?emp_id=${empId}`);
      setAttachments(res.data.attachments);
    } catch (err) {
      console.error("Failed to fetch details", err);
    }
  };

  const handleToggleFlag = async (e, emailId) => {
    e.stopPropagation();
    try {
      await api.post(`/communications/${emailId}/flag?emp_id=${empId}`);
      const updatedList = emails.map(email => email.id === emailId ? { ...email, is_flagged: !email.is_flagged } : email);
      setEmails(updatedList);
      
      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail({ ...selectedEmail, is_flagged: !selectedEmail.is_flagged });
      }
    } catch (err) {
      console.error("Failed to toggle flag", err);
    }
  };

  // THE FIX: Securely download attachments using Axios so it includes the authentication token
  const handleDownloadAttachment = async (att) => {
    try {
      const response = await api.get(`/communications/attachment/${att.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Failed to download attachment", err);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', width: '100%', gap: 2 }}>
      
      {/* LEFT PANE: Inbox List */}
      <Paper elevation={0} sx={{ width: '350px', display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10394f', mb: 2 }}>{title}</Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
            />
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: '100px' }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="UNREAD">Unread</MenuItem>
              <MenuItem value="READ">Read</MenuItem>
              <MenuItem value="FLAGGED">Flagged</MenuItem>
            </TextField>
          </Box>
        </Box>

        <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
          {filteredEmails.map((email) => (
            <React.Fragment key={email.id}>
              <ListItem 
                button 
                selected={selectedEmail?.id === email.id}
                onClick={() => handleSelectEmail(email)}
                sx={{ 
                  bgcolor: email.is_read ? 'transparent' : '#f0f7ff',
                  borderLeft: email.is_read ? '4px solid transparent' : '4px solid #10394f',
                  '&.Mui-selected': { bgcolor: '#e2e8f0' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {!email.is_read && <CircleIcon sx={{ fontSize: 12, color: '#10394f' }} />}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: email.is_read ? 'normal' : 'bold' }} noWrap>
                        {email.sender_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(email.received_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="textSecondary" noWrap sx={{ fontWeight: email.is_read ? 'normal' : '500' }}>
                      {email.subject}
                    </Typography>
                  }
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ml: 1 }}>
                  <IconButton size="small" onClick={(e) => handleToggleFlag(e, email.id)}>
                    {email.is_flagged ? <FlagIcon fontSize="small" color="error" /> : <OutlinedFlagIcon fontSize="small" color="action" />}
                  </IconButton>
                  {email.attachment_count > 0 && <AttachFileIcon fontSize="small" color="action" />}
                </Box>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
          {filteredEmails.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>No communications found.</Typography>
          )}
        </List>
      </Paper>

      {/* RIGHT PANE: Reading Area */}
      <Paper elevation={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {selectedEmail ? (
          <>
            <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>{selectedEmail.subject}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{selectedEmail.sender_name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {new Date(selectedEmail.received_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>

            {/* THE FIX: Added ref={contentRef} so React can find and manipulate the image tags inside */}
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto', bgcolor: '#fff' }}>
               <div ref={contentRef} dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} style={{ fontFamily: 'inherit', lineHeight: '1.6' }} />
            </Box>

            {attachments.length > 0 && (
              <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Attachments ({attachments.length})</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {attachments.map(att => (
                    <Button 
                      key={att.id}
                      variant="outlined" 
                      size="small" 
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadAttachment(att)} // THE FIX: Secure download handler
                      sx={{ bgcolor: '#fff', textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}
                    >
                      {att.file_name} ({formatBytes(att.size_bytes)})
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
            <Typography variant="h6" color="textSecondary">Select a message to read</Typography>
          </Box>
        )}
      </Paper>

    </Box>
  );
};

export default CommunicationCenter;